/**
 * Client-side validation for Organization Compliance form.
 * Aligns with SubcontractorProfile (UTR, PAYE, company number) and backend HMRC expectations.
 */

/** Strip non-digits; cap at 10 (profile / UTR modal behaviour). */
export function normalizeOrgUtrInput(value) {
  return String(value || '').replace(/\D/g, '').slice(0, 10);
}

/** Empty ok; otherwise exactly 10 digits (matches profile pattern + HMRC test-connection). */
export function isValidUtr(utrDigits) {
  const s = String(utrDigits || '').replace(/\D/g, '');
  if (!s) return true;
  return s.length === 10;
}

/** Shown under PAYE inputs and in validation messages (employer / tax office ref). */
export const PAYE_REFERENCE_INVALID_MESSAGE =
  'PAYE reference must be 3 digits, a forward slash, then your reference code (e.g. 914/VA11166).';

/** Optional HTML5 `pattern` — use with form `noValidate` if you also run custom validation. */
export const PAYE_REFERENCE_HTML_PATTERN = '[0-9]{3}/[A-Za-z0-9]+';

export const PAYE_REFERENCE_MAX_LENGTH = 24;

/**
 * PAYE (Employer / HMRC): 3-digit tax office number, slash, alphanumeric employer reference.
 * Empty string is valid (optional in profile/compliance forms); non-empty must match format.
 * Aligns with backend `123/AB12345`-style checks.
 */
export function isValidPayeReference(value) {
  const s = String(value || '').trim();
  if (!s) return true;
  return /^\d{3}\/[A-Za-z0-9]{1,20}$/i.test(s);
}

/** True when a value is non-empty and formatted as a PAYE reference. */
export function isNonEmptyPayeReference(value) {
  const s = String(value || '').trim();
  if (!s) return false;
  return isValidPayeReference(s);
}

/** UK company registration number: up to 8 digits (profile maxLength 8). */
export function isValidCompanyNumber(value) {
  const s = String(value || '').trim();
  if (!s) return true;
  return /^\d{1,8}$/.test(s);
}

/** Shown under Accounts Office inputs and in validation messages. */
export const ACCOUNTS_OFFICE_REFERENCE_INVALID_MESSAGE =
  'Accounts Office reference should match HMRC format (e.g. 914PV00196767). Use 10–14 characters: 3 digits followed by letters and numbers.';

export const ACCOUNTS_OFFICE_REFERENCE_MAX_LENGTH = 14;

/**
 * Accounts Office Reference — HMRC-style alphanumeric refs (e.g. 914PV00196767).
 * Empty ok; if present require 10–14 chars, 3 digits then letters/digits.
 */
export function isValidAccountsOfficeReference(value) {
  const s = String(value || '').trim().toUpperCase().replace(/\s+/g, '');
  if (!s) return true;
  if (s.length < 10 || s.length > 14) return false;
  return /^[0-9]{3}[A-Z0-9]{7,11}$/.test(s);
}

/** UK postcode (loose); empty ok. */
export function isValidUkPostcode(value) {
  const raw = String(value || '').trim();
  if (!raw) return true;
  const norm = raw.toUpperCase().replace(/\s+/g, ' ');
  return /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/.test(norm);
}

/**
 * @param {object} data — org form fields (same keys as orgFormData)
 * @param {{ requirePayeAndAccountsOffice?: boolean }} [options] — when true, PAYE and Accounts Office must be non-empty and valid (organisation compliance / employer setup).
 * @returns {{ errors: Record<string, string>, isValid: boolean }}
 */
export function validateOrgComplianceForm(data, options = {}) {
  const { requirePayeAndAccountsOffice = false } = options;
  const errors = {};

  const utr = normalizeOrgUtrInput(data.utr);
  if (!isValidUtr(utr)) {
    errors.utr = 'UTR must be exactly 10 digits (or leave blank).';
  }

  const payeTrim = String(data.paye_reference || '').trim();
  if (requirePayeAndAccountsOffice) {
    if (!payeTrim) {
      errors.paye_reference = 'PAYE reference is required (e.g. 914/VA11166).';
    } else if (!isValidPayeReference(payeTrim)) {
      errors.paye_reference = PAYE_REFERENCE_INVALID_MESSAGE;
    }
  } else if (!isValidPayeReference(data.paye_reference)) {
    errors.paye_reference = PAYE_REFERENCE_INVALID_MESSAGE;
  }

  if (!isValidCompanyNumber(data.company_number)) {
    errors.company_number = 'Company number must be 1–8 digits only.';
  }

  const aoTrim = String(data.accounts_office_reference || '').trim();
  if (requirePayeAndAccountsOffice) {
    if (!aoTrim) {
      errors.accounts_office_reference = 'Accounts Office reference is required (e.g. 914PV00196767).';
    } else if (!isValidAccountsOfficeReference(aoTrim)) {
      errors.accounts_office_reference = ACCOUNTS_OFFICE_REFERENCE_INVALID_MESSAGE;
    }
  } else if (!isValidAccountsOfficeReference(data.accounts_office_reference)) {
    errors.accounts_office_reference = ACCOUNTS_OFFICE_REFERENCE_INVALID_MESSAGE;
  }

  if (!isValidUkPostcode(data.postal_code)) {
    errors.postal_code = 'Enter a valid UK postcode (e.g. SW1A 1AA).';
  }

  return { errors, isValid: Object.keys(errors).length === 0 };
}
